import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, X, Clock } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import JsonLd from "@/components/JsonLd";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Reveal, Overline } from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import { PACKAGES } from "@/lib/data";
import { PACKAGE_DETAILS } from "@/lib/details";
import { useCms } from "@/context/ContentContext";
import { SITE_URL, waLink } from "@/lib/site";
import { dedupeBySlug } from "@/lib/cms";
import { RelatedGuides } from "@/components/RelatedGuides";

// Maps a journey's region string to its destination guide, DMC and tourism-hub
// pages so every package passes ranking strength across the site architecture.
const REGION_MAP = [
  { match: "meghalaya", name: "Meghalaya", dest: "/destinations/meghalaya", dmc: "/meghalaya-dmc", hub: "/meghalaya-tourism" },
  { match: "sikkim", name: "Sikkim", dest: "/destinations/sikkim", dmc: "/sikkim-dmc", hub: "/sikkim-tourism" },
  { match: "assam", name: "Assam", dest: "/destinations/assam", dmc: "/assam-dmc", hub: "/assam-tourism" },
  { match: "arunachal", name: "Arunachal Pradesh", dest: "/destinations/arunachal-pradesh", dmc: "/arunachal-pradesh-dmc", hub: "/arunachal-pradesh-tourism" },
  { match: "nagaland", name: "Nagaland", dest: "/destinations/nagaland", dmc: "/nagaland-dmc", hub: "/nagaland-tourism" },
  { match: "bhutan", name: "Bhutan", dest: "/destinations/bhutan", dmc: "/bhutan-dmc", hub: "/bhutan-tourism" },
  { match: "nepal", name: "Nepal", dest: "/destinations/nepal", dmc: null, hub: null },
  { match: "bengal", name: "Darjeeling", dest: "/destinations/darjeeling", dmc: "/darjeeling-dmc", hub: "/darjeeling-tourism" },
];

function regionLinksFor(region) {
  const r = String(region || "").toLowerCase();
  const hits = REGION_MAP.filter((m) => r.includes(m.match));
  const links = [];
  for (const h of hits) {
    links.push({ label: `${h.name} travel guide`, to: h.dest });
    if (h.dmc) links.push({ label: `${h.name} DMC — net rates`, to: h.dmc });
    if (h.hub) links.push({ label: `${h.name} tourism hub`, to: h.hub });
  }
  if (hits.length !== 1) links.push({ label: "North East India DMC", to: "/northeast-dmc" });
  return links;
}

export default function PackageDetail() {
  const { slug } = useParams();
  const cms = useCms();
  const cmsPkg = cms.packages.find((p) => p.slug === slug);
  const pkg = cmsPkg || PACKAGES.find((p) => p.slug === slug);
  const detail = cmsPkg || PACKAGE_DETAILS[slug];
  usePageMeta({
    title: pkg ? `${(pkg.region || "North East India").split(" · ")[0]} Tour Package for Travel Agents — ${pkg.title} (${pkg.days}) | TripTrix Voyages DMC` : "Journey | TripTrix Voyages",
    description: pkg ? `${pkg.title} — a ${pkg.days} ${pkg.region} package for travel agents. Day-wise itinerary, inclusions & B2B net rates on request from TripTrix Voyages.` : "",
    image: pkg ? pkg.image : undefined,
    type: "article",
  });

  if (!cms.loaded && !(pkg && detail)) {
    return <div className="min-h-screen grid place-items-center pt-32 text-[var(--tx-text-2)]">Loading…</div>;
  }
  if (!pkg || !detail) return <Navigate to="/packages" replace />;

  const allOthers = dedupeBySlug(PACKAGES, cms.packages).filter((p) => p.slug !== slug);
  const others = [
    ...allOthers.filter((p) => p.region === pkg.region),
    ...allOthers.filter((p) => p.region !== pkg.region),
  ].slice(0, 3);
  const regionName = (pkg.region || "North East India").split(" · ")[0];
  const regionLinks = regionLinksFor(pkg.region);

  // Long-tail agent FAQs (rule-based, unique per package) — keyword depth + FAQ schema.
  const faqs = [
    { q: `What is the net rate for the ${pkg.title} (${pkg.days})?`, a: `Net rates for the ${pkg.title} are shared only with registered travel agents and tour operators. Share your client's dates, pax count and preferred hotel category and our trade desk replies with a workable net quote — usually within a few working hours. There are no retail prices on this site.` },
    { q: `Can the ${pkg.title} itinerary be customised for my clients?`, a: `Yes. Every ${regionName} itinerary is fully customisable — hotel category, pacing, add-on experiences and extensions — and is delivered white-labelled under your brand, so your client only ever sees your agency's name.` },
    { q: `What is the best time to run this ${regionName} tour?`, a: `${regionName} sells year-round, but the clearest skies and easiest road conditions usually run from October to May. Monsoon departures (June–September) suit travellers chasing lush landscapes and lighter crowds — our ground team advises on routing either way.` },
    { q: `Are permits, hotels and transport included in this ${pkg.days} package?`, a: `Yes — as your ground DMC we handle permits (ILP / Protected Area Permits where applicable), contracted hotels and vetted homestays, airport and railway transfers and licensed local guides. The inclusions list on this page shows exactly what the quoted net rate covers.` },
    { q: `Is the ${pkg.title} suitable for FITs, honeymoons and groups?`, a: `Absolutely. We operate this route for FIT couples, honeymooners, families and group series departures. For groups and MICE movements we add coach logistics, meal plans and tour-escort support — all at net rates.` },
    { q: `How do I book the ${pkg.title} for my client?`, a: `Send an enquiry through the form on this page or WhatsApp our trade desk with dates and pax. We confirm availability and net rates within 24 hours, then hold services while you close your client.` },
  ];

  const jsonld = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "TouristTrip",
        name: pkg.title,
        description: pkg.summary,
        image: pkg.image,
        url: `${SITE_URL}/packages/${slug}`,
        touristType: "Leisure",
        provider: { "@type": "TravelAgency", name: "TripTrix Voyages", url: SITE_URL },
        itinerary: {
          "@type": "ItemList",
          numberOfItems: (detail.itinerary || []).length,
          itemListElement: (detail.itinerary || []).map((d, i) => ({
            "@type": "ListItem", position: i + 1, name: d.title,
          })),
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Tour Packages", item: `${SITE_URL}/packages` },
          { "@type": "ListItem", position: 3, name: pkg.title, item: `${SITE_URL}/packages/${slug}` },
        ],
      },
      {
        "@type": "FAQPage",
        datePublished: "2026-06-01",
        dateModified: "2026-09-01",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <>
      <JsonLd data={jsonld} id="jsonld-package" />

      <section className="relative h-[64vh] min-h-[480px] overflow-hidden" data-testid="package-detail-hero">
        <img src={pkg.image} alt={pkg.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--tx-bg)] via-[var(--tx-bg)]/30 to-[var(--tx-bg)]/50" />
        <div className="relative z-10 flex h-full items-end">
          <div className="mx-auto w-full max-w-[1400px] px-6 md:px-10 pb-14">
            <Breadcrumbs
              items={[
                { label: "Home", to: "/" },
                { label: "Tour Packages", to: "/packages" },
                { label: pkg.title },
              ]}
            />
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="text-xs uppercase tracking-[0.24em] text-[var(--tx-gold)]">{pkg.region} · {pkg.days}</span>
              <h1 className="mt-3 font-display font-light tracking-tight text-4xl md:text-6xl lg:text-7xl max-w-4xl">{pkg.title}</h1>
              <div className="mt-5 flex flex-wrap gap-2">
                {(pkg.tags || []).map((t) => (<span key={t} className="rounded-full bg-[var(--tx-bg)]/70 backdrop-blur px-3 py-1 text-xs">{t}</span>))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid gap-12 lg:grid-cols-12">
          {/* Main column */}
          <div className="lg:col-span-8">
            <Reveal>
              <div className="space-y-5 text-lg text-[var(--tx-text-2)] leading-relaxed">
                {detail.overview.map((p, i) => (<p key={i}>{p}</p>))}
              </div>
            </Reveal>

            <div className="mt-14">
              <Reveal><Overline>Day by day</Overline></Reveal>
              <div className="mt-8 relative border-l border-[var(--tx-border)] ml-3">
                {detail.itinerary.map((d, i) => (
                  <Reveal key={d.day} delay={(i % 4) * 0.05}>
                    <div className="relative pl-10 pb-12 last:pb-0 group">
                      <span className="absolute -left-[13px] top-1.5 grid h-6 w-6 place-items-center rounded-full border border-[var(--tx-gold)]/60 bg-[var(--tx-bg)] text-[10px] font-medium tracking-wider text-[var(--tx-gold)] transition-colors duration-300 group-hover:bg-[var(--tx-gold)] group-hover:text-[var(--tx-bg)]">{String(d.day).padStart(2, "0")}</span>
                      <div className="font-display text-3xl font-light text-[var(--tx-gold)]/90 leading-none">Day {String(d.day).padStart(2, "0")}</div>
                      <h3 className="mt-2 font-display text-2xl font-light transition-colors duration-300 group-hover:text-[var(--tx-gold)]">{d.title}</h3>
                      <p className="mt-2 text-[var(--tx-text-2)] leading-relaxed max-w-2xl">{d.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            <div className="mt-12 grid gap-6 md:grid-cols-2">
              <Reveal>
                <div className="border border-[var(--tx-border)] bg-[var(--tx-bg-2)] p-7 h-full">
                  <h4 className="font-display text-xl font-light mb-4">What&apos;s included</h4>
                  <ul className="space-y-3">
                    {detail.inclusions.map((x) => (<li key={x} className="flex gap-3 text-[var(--tx-text-2)]"><Check className="h-5 w-5 shrink-0 text-[var(--tx-gold)]" />{x}</li>))}
                  </ul>
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <div className="border border-[var(--tx-border)] bg-[var(--tx-bg-2)] p-7 h-full">
                  <h4 className="font-display text-xl font-light mb-4">Not included</h4>
                  <ul className="space-y-3">
                    {detail.exclusions.map((x) => (<li key={x} className="flex gap-3 text-[var(--tx-text-2)]"><X className="h-5 w-5 shrink-0 text-[var(--tx-text-2)]/60" />{x}</li>))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Sticky booking card */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <Reveal delay={0.1}>
                <div className="border border-[var(--tx-border)] bg-[var(--tx-bg-2)] p-7">
                  <div className="flex items-center gap-2 text-[var(--tx-text-2)] text-sm"><Clock className="h-4 w-4 text-[var(--tx-gold)]" /> {pkg.days}</div>
                  <div className="mt-4">
                    <span className="font-display text-3xl font-light text-[var(--tx-gold)]">Net rates on request</span>
                  </div>
                  <p className="mt-2 text-xs text-[var(--tx-text-2)]">B2B / agent net pricing shared on enquiry. {detail.priceNote}</p>
                  <div className="mt-4 flex flex-wrap gap-2" data-testid="package-trade-badges">
                    {["Direct DMC operations", "Permit specialists", "24/7 ground support"].map((b) => (
                      <span key={b} className="text-[10px] uppercase tracking-[0.15em] border border-[var(--tx-border)] px-2.5 py-1 text-[var(--tx-text-2)]">{b}</span>
                    ))}
                  </div>
                  <a href="#book" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--tx-gold)] px-6 py-3.5 text-sm font-medium text-[var(--tx-bg)] hover:bg-[var(--tx-text)] transition-colors duration-300">Enquire & book <ArrowUpRight className="h-4 w-4" /></a>
                  <a href={waLink(`Hi TripTrix, I'm interested in the '${pkg.title}' (${pkg.days}) journey.`)} target="_blank" rel="noreferrer" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-[var(--tx-border)] px-6 py-3.5 text-sm hover:border-[var(--tx-gold)] hover:text-[var(--tx-gold)] transition-colors duration-300">WhatsApp this trip</a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Booking form */}
      <section id="book" className="py-20 bg-[var(--tx-bg-2)] border-y border-[var(--tx-border)]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal><Overline>Book this journey</Overline></Reveal>
            <Reveal delay={0.1}><h2 className="mt-6 font-display font-light tracking-tight text-4xl md:text-5xl leading-tight">Reserve your {pkg.region} adventure</h2></Reveal>
            <Reveal delay={0.2}><p className="mt-6 text-lg text-[var(--tx-text-2)] max-w-md">Every itinerary is fully customisable. Send an enquiry and our trade desk confirms availability and net rates within 24 hours.</p></Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.15}>
              <div className="border border-[var(--tx-border)] bg-[var(--tx-bg)] p-7 md:p-9"><EnquiryForm /></div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQs — long-tail keyword depth */}
      <section className="py-16 md:py-24 border-t border-[var(--tx-border)]" data-testid="package-faqs">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal><Overline>Agent FAQs</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display font-light tracking-tight text-3xl sm:text-4xl">{pkg.title} — questions from the trade</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-5">
            {faqs.map((f, i) => (
              <Reveal key={f.q}>
                <div className="border border-[var(--tx-border)] p-6" data-testid={`package-faq-${i}`}>
                  <h3 className="font-display text-xl font-light">{f.q}</h3>
                  <p className="mt-3 text-sm text-[var(--tx-text-2)] leading-relaxed">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Internal links: destination guides, DMC pages & tourism hubs */}
      <section className="py-14 border-t border-[var(--tx-border)]" data-testid="package-internal-links">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--tx-text-2)]">Keep exploring for your clients</div>
          <div className="mt-5 flex flex-wrap gap-3">
            {regionLinks.map((l) => (
              <Link key={l.to + l.label} to={l.to} data-testid={`package-link-${l.to.replace(/\//g, "")}`} className="inline-flex items-center gap-2 border border-[var(--tx-border)] rounded-full px-5 py-2.5 text-sm text-[var(--tx-text-2)] hover:border-[var(--tx-gold)] hover:text-[var(--tx-gold)] transition-colors duration-300">
                {l.label} <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Related guides & stories */}
      <RelatedGuides tokens={[regionName, ...(pkg.tags || [])]} title={`${regionName} guides & stories`} />

      {/* Other journeys */}
      <section className="py-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="flex items-center justify-between mb-8">
            <h3 className="font-display text-2xl font-light">More signature journeys</h3>
            <Link to="/packages" className="text-sm text-[var(--tx-gold)] hover:underline">View all</Link>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {others.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link to={`/packages/${p.slug}`} className="group block border border-[var(--tx-border)] bg-[var(--tx-bg-2)] overflow-hidden hover:border-[var(--tx-gold)]/40 transition-colors duration-500">
                  <div className="relative h-48 overflow-hidden"><img src={p.image} alt={p.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" /></div>
                  <div className="p-6">
                    <span className="text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">{p.days}</span>
                    <h4 className="mt-2 font-display text-xl font-light">{p.title}</h4>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-sm text-[var(--tx-gold)]">Net rates on request</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
